import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { getCopay, initiateCopayPayment, CopayResponse, PaymentInitiateResponse } from "../api/athena";
import AppButton from "../components/AppButton";
import { Alert } from "../../components/shared/Alert";
import { getPayabliSrc, isStageOrDevEnv } from "../../utils/utils";
import { payabliConfigCard, payabliConfigACH, expressCheckoutConfig } from "../../components/linkpayment/Payabli";
import { updatePatientPostPayment } from "../../utils/apis";

const PAYMENT_TABS = {
  CARD: "card",
  BANK_DEBIT: "bank_debit",
  WALLET: 'wallet'
};

const Payments: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [copayData, setCopayData] = useState<CopayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentInitiateResponse | null>(null);
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const [isPayabliOpen, setIsPayabliOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [selectedPaymentTab, setSelectedPaymentTab] = useState(PAYMENT_TABS.CARD);
  const payabliComponentRef = useRef<any>(null);
  const paymentDataRef = useRef<PaymentInitiateResponse | null>(null);
  const callbackExecutedRef = useRef(false);

  useEffect(() => {
    const fetchCopay = async () => {
      if (!appointmentId) {
        setError("Missing appointment ID");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getCopay(appointmentId);
        setCopayData(data);
      } catch (err: any) {
        setError("Unable to load copay information. Please try again.");
        console.error("Failed to fetch copay", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCopay();
  }, [appointmentId]);

  const initializePayabliForm = (payment: PaymentInitiateResponse) => {
    // @ts-ignore
    if (typeof PayabliComponent === "undefined") {
      console.error("Payabli script not loaded.");
      return;
    }

    // Ensure the container exists in DOM
    const container = document.getElementById('pay-component-card');
    if (!container) {
      console.error("Payabli container not found in DOM. Retrying...");
      setTimeout(() => initializePayabliForm(payment), 100);
      return;
    }

    // Reset callback execution flag when initializing new form
    callbackExecutedRef.current = false;

    // Configure Payabli configs
    const configurePayabliConfig = (config: any) => {
      config.token = payment.payabliToken || "";
      config.entryPoint = payment.payabliEntryId || "";
      config.customerData = {
        firstName: payment.firstName || "",
        lastName: payment.lastName || "",
        customerNumber: payment.invoiceNumber || payment.appointmentId || "",
        billingEmail: payment.email || "",
        additionalData: {
          paymentId: payment.paymentId || payment.appointmentId,
          appointmentId: payment.appointmentId,
          type: 'CopayPayment'
        },
      };
      config.functionCallBackSuccess = async (response: any) => {
        // Prevent multiple executions
        if (callbackExecutedRef.current) {
          return;
        }
        callbackExecutedRef.current = true;

        try {
          if (response && response.isSuccess) {
            const referenceId = response?.responseData?.referenceId || '';
            const amount = payment.copay || 0;
            
            // Call backend API to confirm payment
            await updatePatientPostPayment(
              amount,
              referenceId,
              response,
              false, // isScheduled
              undefined, // scheduledStartDate
              undefined, // frequency
              payment.lastName, // guarantorLastName
              undefined // guarantorNumber
            );

            setPaymentStatus({
              ...payment,
              status: "success",
              message: "Your payment request was successful! Your payment will take 2-3 business days to process.",
            });
            setIsPayabliOpen(false);
          } else {
            setError("Payment failed. Please try again.");
            setIsPayabliOpen(false);
          }
        } catch (err: any) {
          setError(err?.response?.data?.message || "Payment was processed but confirmation failed. Please contact support.");
          console.error("Failed to confirm payment", err);
          setIsPayabliOpen(false);
        } finally {
          setIsPaying(false);
          setInitiatingPayment(false);
        }
      };
      config.functionCallBackError = async (errors: any) => {
        // Prevent multiple executions
        if (callbackExecutedRef.current) {
          return;
        }
        callbackExecutedRef.current = true;

        setError("There was an error processing your payment. Please try again.");
        console.error("Payabli error:", errors);
        setIsPayabliOpen(false);
        setIsPaying(false);
        setInitiatingPayment(false);
      };
    };

    // Configure all configs (card, ACH, and express checkout)
    configurePayabliConfig(payabliConfigCard);
    configurePayabliConfig(payabliConfigACH);
    configurePayabliConfig(expressCheckoutConfig);
    
    // Configure expressCheckoutConfig with payment amount
    if (expressCheckoutConfig.expressCheckout) {
      const paymentAmount = payment.copay || 0;
      (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
    }

    try {
      // Cleanup existing instance before creating new one
      if (payabliComponentRef.current) {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
        payabliComponentRef.current = null;
      }

      // Create new instance with the appropriate config based on selected tab
      let initialConfig;
      switch (selectedPaymentTab) {
        case PAYMENT_TABS.BANK_DEBIT:
          initialConfig = payabliConfigACH;
          break;
        case PAYMENT_TABS.WALLET:
          initialConfig = expressCheckoutConfig;
          break;
        default:
          initialConfig = payabliConfigCard;
      }

      // Create new Payabli component instance
      // @ts-ignore
      payabliComponentRef.current = new PayabliComponent(initialConfig);
    } catch (error) {
      console.error("Error initializing Payabli component:", error);
      setError("There was an error initializing the payment form. Please try again.");
      setIsPayabliOpen(false);
      setIsPaying(false);
      setInitiatingPayment(false);
    }
  };

  const loadPayabliScript = (payment: PaymentInitiateResponse) => {
    if (!payment.payabliToken || !payment.payabliEntryId) {
      setError("Payment configuration is missing. Please try again.");
      return;
    }

    paymentDataRef.current = payment;

    // Clean up any existing instance
    if (payabliComponentRef.current) {
      // @ts-ignore
      if (payabliComponentRef.current.cleanup) {
        payabliComponentRef.current.cleanup();
      }
      // @ts-ignore
      if (payabliComponentRef.current.destroy) {
        payabliComponentRef.current.destroy();
      }
      payabliComponentRef.current = null;
    }

    // Open modal first so the DOM element exists
    setIsPayabliOpen(true);

    // Initialize Payabli after modal is rendered in DOM
    const initializeAfterRender = () => {
      // Check if the container exists in DOM
      const container = document.getElementById('pay-component-card');
      if (!container) {
        // If container doesn't exist yet, wait a bit and try again
        setTimeout(initializeAfterRender, 50);
        return;
      }

      // Load Payabli script if not already loaded
      if (!document.querySelector('script[src*="payabli.com/component.js"]')) {
        const script = document.createElement("script");
        script.src = getPayabliSrc();
        if (isStageOrDevEnv()) {
          script.dataset.test = "true";
        }
        script.onload = () => {
          // Small delay to ensure DOM is ready
          setTimeout(() => initializePayabliForm(payment), 100);
        };
        document.body.appendChild(script);
      } else {
        // Script already loaded, initialize after small delay
        setTimeout(() => initializePayabliForm(payment), 100);
      }
    };

    // Use requestAnimationFrame to ensure modal is rendered
    requestAnimationFrame(() => {
      setTimeout(initializeAfterRender, 0);
    });
  };

  const handlePayNow = async () => {
    if (!appointmentId) {
      setError("Missing appointment ID");
      return;
    }

    setInitiatingPayment(true);
    setError(null);
    try {
      const response = await initiateCopayPayment(appointmentId);
      
      // Check if Payabli tokens are available
      if (response.payabliToken && response.payabliEntryId) {
        // Load Payabli script and open payment modal
        loadPayabliScript(response);
      } else {
        // Fallback: just show success message if API doesn't return Payabli tokens
        setPaymentStatus(response);
      }
    } catch (err: any) {
      setError("Unable to initiate payment. Please try again.");
      console.error("Failed to initiate payment", err);
    } finally {
      setInitiatingPayment(false);
    }
  };

  // Create callback functions for payments
  const createCallbackFunctions = (isWallet: boolean = false) => {
    const functionCallBackSuccess = async (response: any) => {
      if (callbackExecutedRef.current) return;
      callbackExecutedRef.current = true;

      try {
        const referenceId = isWallet ? response?.data?.responseData?.referenceId : response?.responseData?.referenceId || '';
        const amount = paymentDataRef.current?.copay || 0;
        
        await updatePatientPostPayment(
          amount,
          referenceId,
          isWallet ? response.data : response,
          false, // isScheduled
          undefined, // scheduledStartDate
          undefined, // frequency
          paymentDataRef.current?.lastName, // guarantorLastName
          undefined // guarantorNumber
        );

        setPaymentStatus({
          ...paymentDataRef.current!,
          status: "success",
          message: "Your payment request was successful! Your payment will take 2-3 business days to process.",
        });
        setIsPayabliOpen(false);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Payment was processed but confirmation failed. Please contact support.");
        console.error("Failed to confirm payment", err);
        setIsPayabliOpen(false);
      } finally {
        setIsPaying(false);
        setInitiatingPayment(false);
      }
    };

    const functionCallBackError = async (errors: any) => {
      if (callbackExecutedRef.current) return;
      callbackExecutedRef.current = true;

      setError("There was an error processing your payment. Please try again.");
      console.error("Payabli error:", errors);
      setIsPayabliOpen(false);
      setIsPaying(false);
      setInitiatingPayment(false);
    };

    return { functionCallBackSuccess, functionCallBackError };
  };

  const handlePayClick = () => {
    if (isPaying || !payabliComponentRef.current) return;
    
    setIsPaying(true);
    setIsPayabliOpen(false);

    if (payabliComponentRef.current && paymentDataRef.current) {
      const amount = paymentDataRef.current.copay || 0;
      
      // Validate amount before proceeding
      if (!amount || amount <= 0) {
        console.error('Invalid payment amount:', amount);
        setError('Invalid payment amount. Please try again.');
        setIsPaying(false);
        return;
      }

      const paymentMethod = selectedPaymentTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
      
      // Update expressCheckoutConfig if wallet payment is selected
      if (selectedPaymentTab === PAYMENT_TABS.WALLET) {
        const updatedExpressConfig = { ...expressCheckoutConfig };
        if (updatedExpressConfig.expressCheckout) {
          (updatedExpressConfig.expressCheckout as any).amount = amount;
        }
        payabliComponentRef.current.updateConfig(updatedExpressConfig);
      }

      // @ts-ignore
      payabliComponentRef.current.payabliExec(paymentMethod, {
        paymentDetails: {
          totalAmount: amount,
          serviceFee: 0,
          categories: [{ 
            label: "Copay", 
            amount: amount, 
            qty: 1 
          }],
        },
      });
    } else {
      console.error("PayabliComponent is not initialized.");
      setIsPaying(false);
    }
  };

  // Handle tab switching - update Payabli config
  useEffect(() => {
    // Only update config if component is already initialized and modal is open
    // Don't run on initial modal open (that's handled by initializePayabliForm)
    if (!payabliComponentRef?.current || !isPayabliOpen || !paymentDataRef.current) return;

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      try {
        // Cleanup existing instance before updating config
        if (payabliComponentRef.current) {
          // @ts-ignore
          if (payabliComponentRef.current.cleanup) {
            payabliComponentRef.current.cleanup();
          }
        }

        if (selectedPaymentTab === PAYMENT_TABS.CARD) {
          // @ts-ignore
          payabliComponentRef.current.updateConfig(payabliConfigCard);
        } else if (selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT) {
          // @ts-ignore
          payabliComponentRef.current.updateConfig(payabliConfigACH);
        } else {
          // Update expressCheckoutConfig with current payment amount and callbacks before applying
          const { functionCallBackSuccess, functionCallBackError } = createCallbackFunctions(true);
          const paymentAmount = paymentDataRef.current?.copay || 0;
          (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
          expressCheckoutConfig.functionCallBackSuccess = functionCallBackSuccess;
          expressCheckoutConfig.functionCallBackError = functionCallBackError;
          // @ts-ignore
          payabliComponentRef.current.updateConfig(expressCheckoutConfig);
        }
      } catch (error) {
        console.error('Error updating payabli config:', error);
        // If there's an error, reinitialize the component
        if (paymentDataRef.current) {
          initializePayabliForm(paymentDataRef.current);
        }
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedPaymentTab]);

  const closePopup = () => {
    setIsPayabliOpen(false);
    setIsPaying(false);
    // Reset callback execution flag
    callbackExecutedRef.current = false;
    // Reset selected payment tab to default
    setSelectedPaymentTab(PAYMENT_TABS.CARD);
    // Cleanup the component
    if (payabliComponentRef.current) {
      try {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
      } catch (error) {
        console.error('Error cleaning up payabli component:', error);
      }
      payabliComponentRef.current = null;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        <h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">
          Payments
        </h1>
        <div className="text-xl text-[#1C1C1C] text-center mb-6">
          Your information has been saved successfully
        </div>
        <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[600px] flex flex-col gap-6 mt-4">
          {loading && (
            <div className="text-center text-[#5D6A85]">
              Loading copay information...
            </div>
          )}

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          {paymentStatus && (
            <Alert type="success" className="mb-4">
              {paymentStatus.message}
            </Alert>
          )}

          {!loading && copayData && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <p className="text-lg text-[#1C1C1C] mb-2">
                  Thank you for completing your check-in.
                </p>
              </div>

              <div className="border-t border-[#A4BDC0] pt-6">
                <div className="text-center mb-6">
                  <p className="text-lg text-[#1C1C1C] mb-2">
                    You have a copay amount due
                  </p>
                  <p className="text-3xl font-bold text-[#01545D]">
                    {formatCurrency(copayData.copay)}
                  </p>
                </div>

                {!paymentStatus && (
                  <div className="flex justify-center">
                    <AppButton
                      onClick={handlePayNow}
                      disabled={initiatingPayment}
                      className="min-w-[200px]"
                    >
                      {initiatingPayment ? "Processing..." : "Pay Now"}
                    </AppButton>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payabli Payment Modal */}
          {isPayabliOpen && (
            <div className="fixed bg-black/50 inset-0 flex justify-center items-center z-50">
              <div className="w-[95%] max-w-sm sm:max-w-md mx-auto border-2 rounded-lg p-3 sm:p-6 bg-white shadow-lg relative">
                <button
                  className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-lg sm:text-xl p-2"
                  onClick={closePopup}
                >
                  ✕
                </button>
                <h4 className="text-base sm:text-lg font-bold text-gray-700 mb-4 text-center pt-2">Payment Method</h4>

                <div className="tabs mb-4 flex border-b overflow-x-auto">
                  <button
                    className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                      selectedPaymentTab === PAYMENT_TABS.CARD 
                        ? "border-b-2 border-[#01545D] text-[#01545D]" 
                        : "text-gray-500"
                    }`}
                    onClick={() => setSelectedPaymentTab(PAYMENT_TABS.CARD)}
                  >
                    Payment Card
                  </button>
                  <button
                    className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                      selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT 
                        ? "border-b-2 border-[#01545D] text-[#01545D]" 
                        : "text-gray-500"
                    }`}
                    onClick={() => setSelectedPaymentTab(PAYMENT_TABS.BANK_DEBIT)}
                  >
                    Bank Debit
                  </button>
                  <button
                    className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                      selectedPaymentTab === PAYMENT_TABS.WALLET 
                        ? "border-b-2 border-[#01545D] text-[#01545D]" 
                        : "text-gray-500"
                    }`}
                    onClick={() => setSelectedPaymentTab(PAYMENT_TABS.WALLET)}
                  >
                    Wallets
                  </button>
                </div>

                <div className="min-h-[300px] sm:min-h-[400px]">
                  <div id="pay-component-card"></div>
                </div>
                <button
                  id="btn-pay"
                  className={`w-full py-2.5 sm:py-3 bg-[#01545D] text-white font-bold text-sm sm:text-base uppercase rounded-md flex items-center justify-center mt-4 hover:bg-[#014047] transition-colors ${
                    selectedPaymentTab === PAYMENT_TABS.WALLET ? 'hidden' : ''
                  } ${isPaying ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isPaying}
                  onClick={handlePayClick}
                >
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAPxJREFUSEvtldGNwjAMhr9/AnQTAJNwbMAG9CZATAC3ARvABjACN8mVDW4DI6Mg9VqStEWVeMAvjSrb3x8ndsTApoHz0wpgZgtgBXwGQWfgW5J/k5YFmNkBWEayOGSbIiQBQfkxJFgDp7AugE1Yz1M7yQG8BDNgLWlXVWpmrtwhP5LupWtsJgewEPEh6a8GmAC//k9SNE8rQCyBmd0EDAbI3aAbPOXURmEO0gCYmR/YHvAad7ES+KrfqEcAdxx3yVzxLSVNq7GPAPeb04tRP/A3oNm5taZ7l+g1S+RTc9SrCeAi6d8EiI0Kf8W6dvMFKLKjoqfyaFj2TX4WeAX0MmsZ8nx06AAAAABJRU5ErkJggg=="
                    alt="Save Payment Icon"
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                  {isPaying ? "Processing..." : "Pay"}
                </button>
              </div>
            </div>
          )}

          {!loading && !copayData && !error && (
            <div className="text-center">
              <p className="text-lg text-[#1C1C1C] mb-2">
                Thank you for completing your check-in.
              </p>
              <p className="text-sm text-[#5D6A85]">
                Your progress has been saved and you can return at any time to continue.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Payments;